import React from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import { ICustomField } from '../../../types';
import { sharedClasses } from '../../../../Components/CustomUIElements/sharedClasses';
import {
  FormTextField,
  FormNumberField
} from '../../../../Components/CustomUIElements/FormTextField';
import { styles } from '../../styles';

interface CustomFieldRendererProps {
  field: ICustomField;
  customFieldAnswers: Record<string, { value: string | string[]; type: string }>;
  setCustomFieldAnswer: (answer: string | string[]) => void;
}

export const CustomFieldRenderer = ({
  field,
  customFieldAnswers,
  setCustomFieldAnswer
}: CustomFieldRendererProps) => {
  switch (field.type) {
    case 'NUMBER':
      return (
        <Stack sx={{ width: '48.5%' }}>
          <FormNumberField
            label={field.name}
            value={
              typeof customFieldAnswers[field.api_name]?.value === 'string'
                ? (customFieldAnswers[field.api_name].value as string)
                : ''
            }
            onChange={(e) => setCustomFieldAnswer(e.target.value)}
            fullWidth
          />
        </Stack>
      );
    case 'TEXT':
      return (
        <Stack sx={{ width: '48.5%' }}>
          <FormTextField
            label={field.name}
            value={
              typeof customFieldAnswers[field.api_name]?.value === 'string'
                ? (customFieldAnswers[field.api_name].value as string)
                : ''
            }
            onChange={(e) => setCustomFieldAnswer(e.target.value)}
            fullWidth
          />
        </Stack>
      );
    case 'SINGLE_SELECT':
      return (
        <Autocomplete
          disablePortal
          options={field.options || []}
          value={field.options?.find(
            (option) => option.label === customFieldAnswers[field.api_name]?.value
          )}
          sx={{
            ...sharedClasses.formAutocomplete,
            width: '48.5%',
            marginTop: 3,
            paddingRight: '2px',
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              padding: '7px 4px 7px 11px'
            }
          }}
          onChange={(e, value) => setCustomFieldAnswer(value?.label || '')}
          slotProps={styles.autocompleteSlotProps}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.name}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />
      );
    case 'MULTI_SELECT':
      return (
        <Autocomplete
          disablePortal
          multiple
          options={field.options}
          value={field.options.filter((option) => {
            const value = customFieldAnswers[field.api_name]?.value;
            return Array.isArray(value) && value.length > 0
              ? (value as string[]).includes(option.label)
              : false;
          })}
          sx={{
            ...sharedClasses.formAutocomplete,
            width: '48.5%',
            marginTop: 3,
            paddingRight: '2px',
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              padding: '7px 4px 7px 11px'
            }
          }}
          onChange={(e, value) => setCustomFieldAnswer(value.map((v) => v.label))}
          slotProps={styles.autocompleteSlotProps}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.name}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />
      );
    default:
      return <Stack sx={{ width: '100%' }}></Stack>;
  }
};
